import { useCallback, useEffect, useMemo, useState } from 'react'
import OrgView from './OrgView'
import StationView from './StationView'
import Drawer from './Drawer'
import CommandPalette from './CommandPalette'
import {
  StationForm, NewStationForm, NewLineForm, TankForm, NewTankForm, PumpForm, NewPumpForm, LineForm,
} from './forms'
import { PRODUCTS, productLines } from './constants'
import { latestTankLevels } from '../../utils/dataHelpers'

const CLOSED = { kind: null }

/**
 * The Station Calibration console: an organisation, its stations, and each
 * station's product lines. Every edit happens in the right-hand drawer, so
 * nothing is written by a stray click — you open, change, and save.
 */
export default function CalibrationConsole({
  currentUser, organizations, branches, entries,
  nav, onNavChange, showToast,
  onSetValve, onSetCalibration, onAddProductLine, onRetireProductLine,
  onAddTank, onUpdateTank, onRetireTank,
  onAddPump, onUpdatePump, onRetirePump,
  onAddStation, onUpdateStation, onDeleteStation,
}) {
  const [drawer, setDrawer] = useState(CLOSED)
  const [busy, setBusy] = useState(false)
  const [paletteOpen, setPaletteOpen] = useState(false)

  const stations = useMemo(() => (
    currentUser.role === 'superadmin'
      ? branches
      : branches.filter((b) => b.organizationId === currentUser.organizationId)
  ), [branches, currentUser])

  const levels = useMemo(() => latestTankLevels(entries), [entries])

  const station = nav.stationId ? stations.find((s) => s.id === nav.stationId) : null
  const view = station ? 'station' : 'org'

  // A superadmin spans organisations; scope to the one being looked at.
  const orgId = station?.organizationId || currentUser.organizationId || organizations[0]?.id
  const org = organizations.find((o) => o.id === orgId)
  const orgStations = currentUser.role === 'superadmin' && !station
    ? stations
    : stations.filter((s) => s.organizationId === orgId)

  useEffect(() => {
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPaletteOpen((v) => !v)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  // The palette can act on a station other than the one on screen, so it moves
  // there first and opens the drawer against it.
  const createFromPalette = useCallback(({ kind, stationId, product }) => {
    if (stationId && stationId !== nav.stationId) onNavChange({ view: 'station', stationId })
    setDrawer({ kind, product })
  }, [nav.stationId, onNavChange])

  const closeDrawer = () => setDrawer(CLOSED)
  const lineOf = (product) => productLines(station).find((l) => l.product === product)

  /** Runs a mutation, reports whatever it says, and closes the drawer on success. */
  async function run(fn, successMessage, { keepOpen = false } = {}) {
    setBusy(true)
    const result = await fn()
    setBusy(false)
    if (!result || result.success === false) {
      showToast(result?.message || 'That did not work.', 'error')
      return result
    }
    if (successMessage) showToast(typeof successMessage === 'function' ? successMessage(result) : successMessage)
    if (!keepOpen) closeDrawer()
    return result
  }

  async function toggleValve(line, tank) {
    const opening = !tank.valveOpen
    const result = await onSetValve(tank.id, opening)
    if (!result.success) { showToast(result.message, 'error'); return }
    const after = opening
      ? [...line.tanks.filter((t) => t.valveOpen), tank]
      : line.tanks.filter((t) => t.valveOpen && t.id !== tank.id)
    const names = after.map((t) => `Tank ${t.tankNumber}`).join(' & ')
    showToast(`Tank ${tank.tankNumber} valve ${opening ? 'opened' : 'closed'} — selling ${line.product} from ${names}.`)
  }

  // Provisional: the server assigns the real number, skipping any used by
  // retired hardware. The toast afterwards names what was actually created.
  const nextTankNumber = station ? Math.max(0, ...station.tanks.map((t) => t.tankNumber || 0)) + 1 : 1
  const nextPumpNumber = station ? Math.max(0, ...station.pumps.map((p) => p.pumpNumber || 0)) + 1 : 1

  const d = drawer
  const drawerLine = d.product ? lineOf(d.product) : null
  const drawerTank = drawerLine && d.id ? drawerLine.tanks.find((t) => t.id === d.id) : null
  const drawerPump = drawerLine && d.id ? drawerLine.pumps.find((p) => p.id === d.id) : null

  const titles = {
    station: station?.name,
    newStation: 'Add a station',
    newLine: `Add a product to ${station?.name || ''}`,
    line: `${PRODUCTS[d.product]?.name || d.product} line`,
    tank: drawerTank ? `Tank ${drawerTank.tankNumber}` : '',
    newTank: `Add a tank to ${d.product}`,
    pump: drawerPump?.name || 'Pump',
    newPump: `Add a pump to ${d.product}`,
  }
  const kickers = {
    station: 'Station',
    newStation: 'New station',
    newLine: 'New product line',
    line: `Product line · ${d.product}`,
    tank: `Tank · ${d.product}`,
    newTank: `New tank · ${d.product}`,
    pump: `Pump · ${d.product}`,
    newPump: `New pump · ${d.product}`,
  }

  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '')

  return (
    <>
      {/* The shortcut needs somewhere to be discovered. Hidden on touch layouts,
          where there is no keyboard to press it with. */}
      <div className="mb-3 hidden justify-end desk:flex">
        <button
          type="button"
          onClick={() => setPaletteOpen(true)}
          className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-[12px] text-gray-500 transition hover:border-gray-300 hover:text-gray-700"
        >
          <kbd className="rounded border border-gray-200 bg-gray-50 px-1.5 font-mono text-[10.5px]">{isMac ? '⌘' : 'Ctrl'}</kbd>
          <kbd className="rounded border border-gray-200 bg-gray-50 px-1.5 font-mono text-[10.5px]">K</kbd>
          Go to or add anything
        </button>
      </div>

      {view === 'org' ? (
        <OrgView
          orgName={org?.name || 'Your stations'}
          stations={orgStations}
          levels={levels}
          onOpenStation={(id) => onNavChange({ view: 'station', stationId: id })}
          onAddStation={() => setDrawer({ kind: 'newStation' })}
        />
      ) : (
        <StationView
          station={station}
          levels={levels}
          orgName={org?.name}
          onBack={() => onNavChange({ view: 'org', stationId: null })}
          onOpenTank={(line, tank) => setDrawer({ kind: 'tank', product: line.product, id: tank.id })}
          onAddTank={(line) => setDrawer({ kind: 'newTank', product: line.product })}
          onOpenPump={(line, pump) => setDrawer({ kind: 'pump', product: line.product, id: pump.id })}
          onAddPump={(line) => setDrawer({ kind: 'newPump', product: line.product })}
          onToggleValve={toggleValve}
          onOpenLine={(line) => setDrawer({ kind: 'line', product: line.product })}
          onAddLine={() => setDrawer({ kind: 'newLine' })}
          onEditStation={() => setDrawer({ kind: 'station' })}
        />
      )}

      <Drawer
        open={Boolean(d.kind)}
        kicker={kickers[d.kind]}
        title={titles[d.kind]}
        onClose={closeDrawer}
      >
        {d.kind === 'station' && station && (
          <StationForm
            station={station}
            busy={busy}
            onCancel={closeDrawer}
            onSave={(values) => run(
              () => onUpdateStation(station.id, { ...values, organizationId: station.organizationId }),
              'Station saved.',
            )}
            onDelete={() => run(async () => {
              const r = await onDeleteStation(station.id)
              if (r?.success !== false) onNavChange({ view: 'org', stationId: null })
              return r
            }, `${station.name} deleted.`)}
          />
        )}

        {d.kind === 'newStation' && (
          <NewStationForm
            orgName={org?.name || 'your organisation'}
            orgOptions={currentUser.role === 'superadmin' ? organizations : []}
            defaultOrgId={orgId}
            busy={busy}
            onCancel={closeDrawer}
            onSave={({ name, location, organizationId }) => {
              if (!name.trim()) { showToast('Give the station a name.', 'error'); return undefined }
              return run(
                () => onAddStation({ organizationId: organizationId || orgId, name, location }),
                `${name.trim()} added.`,
              )
            }}
          />
        )}

        {d.kind === 'newLine' && station && (
          <NewLineForm
            station={station}
            busy={busy}
            onCancel={closeDrawer}
            onSave={(values) => run(
              () => onAddProductLine(station.id, values),
              `${values.product} line added — ${values.tankCount} tank${values.tankCount === 1 ? '' : 's'}, ${values.pumpCount} pump${values.pumpCount === 1 ? '' : 's'} on one shared line.`,
            )}
          />
        )}

        {d.kind === 'line' && drawerLine && station && (
          <LineForm
            line={drawerLine}
            station={station}
            busy={busy}
            onCancel={closeDrawer}
            onRetire={() => run(
              () => onRetireProductLine(station.id, drawerLine.product),
              `${drawerLine.product} line retired — its readings are kept.`,
            )}
          />
        )}

        {d.kind === 'tank' && drawerTank && drawerLine && (
          <TankForm
            tank={drawerTank}
            line={drawerLine}
            busy={busy}
            onCancel={closeDrawer}
            onSave={({ capacity }) => run(() => onUpdateTank(drawerTank.id, { capacity }), 'Tank saved.')}
            onToggleValve={async () => { await toggleValve(drawerLine, drawerTank); closeDrawer() }}
            onRetire={() => run(() => onRetireTank(drawerTank.id), `Tank ${drawerTank.tankNumber} retired — its readings are kept.`)}
          />
        )}

        {d.kind === 'newTank' && drawerLine && station && (
          <NewTankForm
            line={drawerLine}
            station={station}
            nextNumber={nextTankNumber}
            busy={busy}
            onCancel={closeDrawer}
            onSave={({ capacity }) => run(
              () => onAddTank(station.id, { product: drawerLine.product, capacity }),
              (r) => `Tank ${r.tankNumber} added to ${drawerLine.product} as reserve.`,
            )}
          />
        )}

        {d.kind === 'pump' && drawerPump && drawerLine && (
          <PumpForm
            pump={drawerPump}
            line={drawerLine}
            busy={busy}
            onCancel={closeDrawer}
            onSave={async ({ name, calibration }) => {
              setBusy(true)
              const a = await onUpdatePump(drawerPump.id, { name })
              const b = a.success === false ? a : await onSetCalibration(drawerPump.id, calibration)
              setBusy(false)
              if (b.success === false) { showToast(b.message, 'error'); return }
              showToast(`Saved — ${name || 'pump'} at ${Number(calibration).toFixed(2)} L per 20 L.`)
              closeDrawer()
            }}
            onRetire={() => run(() => onRetirePump(drawerPump.id), `${drawerPump.name || 'Pump'} retired — its readings are kept.`)}
          />
        )}

        {d.kind === 'newPump' && drawerLine && station && (
          <NewPumpForm
            line={drawerLine}
            station={station}
            nextNumber={nextPumpNumber}
            busy={busy}
            onCancel={closeDrawer}
            onSave={({ name }) => run(
              () => onAddPump(station.id, { product: drawerLine.product, name }),
              `${name || 'Pump'} added to the ${drawerLine.product} line.`,
            )}
          />
        )}
      </Drawer>

      <CommandPalette
        open={paletteOpen}
        stations={orgStations}
        onClose={() => setPaletteOpen(false)}
        onNavigate={onNavChange}
        onCreate={createFromPalette}
      />
    </>
  )
}
